import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    ShoppingBag, User, Phone, MapPin, RefreshCw,
    ChevronDown, ChevronUp, Package, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { useAuth } from '../App';
import { API } from '../App';
import { useNavigate } from 'react-router-dom';

const SalesmanOrders = () => {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    const headers = { Authorization: `Bearer ${token}` };

    const fetchOrders = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/salesman/orders`, { headers });
            setOrders(res.data);
        } catch (e) {
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchOrders(); }, []);

    const statusColor = (s) => ({
        confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30',
        processing: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30',
        shipped: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30',
        delivered: 'bg-green-100 text-green-700 dark:bg-green-900/30',
        cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30',
    }[s] || 'bg-gray-100 text-gray-700');

    const totalRevenue = orders.reduce((s, o) => s + (o.total_amount || 0), 0);

    return (
        <DashboardLayout userRole="salesman">
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <ShoppingBag className="w-7 h-7 text-primary" />
                            Orders I Placed
                        </h1>
                        <p className="text-muted-foreground text-sm mt-1">All orders placed by you on behalf of customers</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" size="icon" onClick={fetchOrders}><RefreshCw className="w-4 h-4" /></Button>
                        <Button className="btn-primary gap-2" onClick={() => navigate('/salesman/place-order')}>
                            <ShoppingBag className="w-4 h-4" />
                            Place New Order
                        </Button>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                        { label: 'Total Orders', value: orders.length, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
                        { label: 'Revenue Generated', value: `₹${totalRevenue.toLocaleString('en-IN')}`, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
                        { label: 'Subsidy Orders', value: orders.filter(o => o.claim_subsidy).length, color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20' },
                    ].map((s, i) => (
                        <div key={i} className={`${s.bg} rounded-xl p-4 border`}>
                            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                            <p className="text-sm text-muted-foreground mt-0.5">{s.label}</p>
                        </div>
                    ))}
                </div>

                {/* Orders List */}
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" />
                    </div>
                ) : orders.length === 0 ? (
                    <div className="text-center py-16 text-muted-foreground bg-card border rounded-xl">
                        <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-20" />
                        <p className="font-medium">No orders placed yet</p>
                        <Button className="btn-primary mt-4" onClick={() => navigate('/salesman/place-order')}>Place First Order</Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {orders.map(o => (
                            <div key={o.id} className="bg-card border rounded-xl overflow-hidden">
                                <div className="p-4 flex items-center justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor(o.status)}`}>{o.status}</span>
                                            {o.claim_subsidy && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700 dark:bg-green-900/30">Subsidy</span>}
                                        </div>
                                        <p className="font-bold">{o.customer?.name || o.customer_name_override || 'Customer'}</p>
                                        <div className="flex gap-3 mt-1 text-sm text-muted-foreground">
                                            {o.customer?.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{o.customer.phone}</span>}
                                            <span className="flex items-center gap-1"><Package className="w-3 h-3" />{o.items?.length} item(s)</span>
                                            <span className="font-semibold text-primary">₹{o.total_amount?.toLocaleString('en-IN')}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">{new Date(o.created_at).toLocaleString('en-IN')}</p>
                                    </div>
                                    <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="text-muted-foreground hover:text-foreground p-1 shrink-0">
                                        {expanded === o.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                    </button>
                                </div>

                                {expanded === o.id && (
                                    <div className="border-t bg-muted/20 p-4 space-y-4">
                                        <div className="grid md:grid-cols-2 gap-3 text-sm">
                                            <div className="space-y-1">
                                                <p className="text-xs font-semibold text-muted-foreground uppercase">Customer</p>
                                                <p><strong>{o.customer?.name}</strong></p>
                                                {o.customer?.phone && <p className="flex items-center gap-1 text-muted-foreground"><Phone className="w-3 h-3" />{o.customer.phone}</p>}
                                                {o.customer?.email && <p className="text-muted-foreground">{o.customer.email}</p>}
                                            </div>
                                            <div className="space-y-1">
                                                <p className="text-xs font-semibold text-muted-foreground uppercase">Address</p>
                                                <p className="flex items-start gap-1 text-muted-foreground"><MapPin className="w-3 h-3 mt-0.5 shrink-0" />{o.shipping_address}</p>
                                                <p><strong>Payment:</strong> {o.payment_method?.replace(/_/g, ' ')}</p>
                                            </div>
                                        </div>

                                        <div className="border rounded-lg overflow-hidden">
                                            <div className="bg-muted/50 px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase">Items</div>
                                            {o.items?.map((item, i) => (
                                                <div key={i} className="flex items-center gap-3 px-3 py-2 border-t text-sm">
                                                    {item.image_url && <img src={item.image_url} alt={item.name} className="w-8 h-8 rounded object-cover" />}
                                                    <span className="flex-1 truncate">{item.name}</span>
                                                    <span className="text-muted-foreground">×{item.quantity}</span>
                                                    <span className="font-semibold">₹{(item.price * item.quantity)?.toLocaleString('en-IN')}</span>
                                                </div>
                                            ))}
                                            <div className="flex justify-between px-3 py-2 border-t bg-muted/20 font-bold text-sm">
                                                <span>Total</span>
                                                <span className="text-primary">₹{o.total_amount?.toLocaleString('en-IN')}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default SalesmanOrders;
